import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "row align-items-center mb-3" }
const _hoisted_3 = { class: "col-md-12" }
const _hoisted_4 = { class: "row align-items-center mb-3" }
const _hoisted_5 = { class: "col-md-4" }
const _hoisted_6 = {
  for: "moneyFund",
  class: "form-label mb-0"
}
const _hoisted_7 = { class: "col-md-3" }
const _hoisted_8 = { class: "row align-items-center mb-3" }
const _hoisted_9 = { class: "col-md-4" }
const _hoisted_10 = {
  for: "moneyFund",
  class: "form-label mb-0"
}
const _hoisted_11 = { class: "col-md-3" }
const _hoisted_12 = { class: "row align-items-center mb-4" }
const _hoisted_13 = { class: "col-md-4" }
const _hoisted_14 = {
  for: "moneyFund",
  class: "form-label mb-0"
}
const _hoisted_15 = { class: "col-md-8" }
const _hoisted_16 = {
  key: 0,
  class: "text-danger ms-2"
}

import { ref, computed, watch, PropType } from "vue";
import { Retiree } from "../../ts/retiree";
import { RetirementPlanResponse } from "@/guiide/ts/response/retirementPlanResponse";
import { useStore } from "vuex";
import { getPensionPotDrawdownAmounts } from "@/guiide/ts/utilities";
import { formatter } from "@/shared/ts/formatter";
import fundInfo from "@/guiide/ts/funds";
import Constants from "@/guiide/ts/constants";
import PatternFillType from "@/guiide/ts/charts/patternFill";
import patternFills from "@/guiide/ts/charts/patternFills";
import { PointOptionsObject } from "highcharts";
import SubSection from "../common/SubSection.vue";
import DrawdownChart from "../charts/DrawdownChart.vue";
import FundAllocationCalculation from "@/guiide/ts/secure/fundAllocationCalculation";
import NumberInput from "../common/NumberInput.vue";
import { CurrentAllocations } from "@/guiide/ts/integration/currentAllocations";
import FundAllocationCalculator from "@/guiide/ts/secure/fundAllocationCalculator";
import { FundAllocationInstruction } from "@/guiide/ts/secure/fundAllocationInstruction";
import { PensionPot } from "@/guiide/ts/pensionPot";


export default /*@__PURE__*/_defineComponent({
  __name: 'InvestmentAllocation',
  props: {
    retiree: { type: Object as () => Retiree | null, default: () => null, },
    selectedPensionPotIds: { type: Array as PropType<Array<number>>, default: () => [] }
},
  emits: ["invalid"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

__expose({ getAllocationInstruction });

const store = useStore();

const matchedYearColor = Constants.Color.lightEmerald;
const unmatchedYearColor = Constants.Color.brunswickGreen;

const matchedPaymentsModel = ref(Constants.Product.MatchedYearsMin);
const drawdownChartPoints = ref<Array<PointOptionsObject>>(new Array<PointOptionsObject>());

const currentAllocToMoneyFund = ref(0);
const currentAllocToIncomeFund = ref(0);
let currentAllocations = ref<CurrentAllocations>(new CurrentAllocations());
const overallocatedAmount = ref(0);

watch(
    () => [currentAllocToMoneyFund.value, currentAllocToIncomeFund.value],
    () => {
        currentAllocations.value.updateAllocations(currentAllocToMoneyFund.value, currentAllocToIncomeFund.value);

        const isOverallocated = currentAllocations.value.growthFund < 0;
        overallocatedAmount.value = isOverallocated ? Math.abs(currentAllocations.value.growthFund) : 0;

        let matchedYears = 0;

        if (!isOverallocated) {
            let remainingLowRiskAllocation = totalLowRiskAllocationAtRetirement.value - currentAllocations.value.taxFreeCashLumpSum;

            for (const drawdownAmount of pensionPotDrawdownAmounts.value) {
                remainingLowRiskAllocation -= drawdownAmount;

                if (remainingLowRiskAllocation < 0) {
                    break;
                }

                matchedYears++;
            }
        }

        matchedPaymentsModel.value = matchedYears;
    }
);

const potsToTransfer = computed<Array<PensionPot>>(() => {
    if (!props.retiree) {
        return [];
    }

    return props.retiree.pensionPots.pots.filter(pp => props.selectedPensionPotIds.includes(pp.id));
});

watch(
    () => props.selectedPensionPotIds,
    () => {
        if (!props.retiree) {
            return;
        }

        const estCurrentValue = potsToTransfer.value.reduce((total, pp) => total + pp.estimatedValueAtPresent, 0);

        let taxFreeCashLumpSum = potsToTransfer.value.reduce((total, pp) => total + pp.lumpSumValueAtRetirement, 0);
        taxFreeCashLumpSum = Math.min(Constants.TaxFreeCashLimit, taxFreeCashLumpSum);

        currentAllocations.value.initialise(estCurrentValue, taxFreeCashLumpSum);

        currentAllocToMoneyFund.value = 0;
        currentAllocToIncomeFund.value = 0;
    },
    { immediate: true }
);

const totalEstimatedValueAtRetirement = computed<number>(() => potsToTransfer.value.reduce((total, pp) => total + pp.estimatedValueAtRetirement, 0));

const totalLowRiskAllocationAtRetirement = computed<number>(() => {
    if (overallocatedAmount.value > 0) {
        return 0;
    }

    return totalEstimatedValueAtRetirement.value * currentAllocations.value.totalLowRiskPercentage;
});

const isTakingTaxFreeLumpSum = computed<boolean>(() => store.state.retiree?.requirements.takingTaxFreeAsLumpSum ?? false);

const pensionPotDrawdownAmounts = computed<Array<number>>(() => getPensionPotDrawdownAmounts(store.state.plan));

const retirementPlan = computed<RetirementPlanResponse>(() => store.state.plan);

const unmatchedPaymentYearsCount = computed<number>(() => pensionPotDrawdownAmounts.value.length - matchedPaymentsModel.value);

watch(
    () => [
        store.state.retiree,
        matchedPaymentsModel.value,
        retirementPlan.value,
        pensionPotDrawdownAmounts.value,
        currentAllocations.value,
        totalLowRiskAllocationAtRetirement.value
    ],
    () => { redraw(); },
    { deep: true, immediate: true }
);

function redraw() {
    if (store.state.retiree == null || retirementPlan.value == null && store.state.plan != null) {
        return;
    }

    updateDrawdownChart();
}

function updateDrawdownChart() {
    drawdownChartPoints.value.length = 0;

    pensionPotDrawdownAmounts.value.map((a, i) => {
        const isMatchedYear = i < matchedPaymentsModel.value;
        const colorName = isMatchedYear ? matchedYearColor : unmatchedYearColor;
        const strokeWidth = 3;
        const pointOptionsObject = getDataPoint(a, colorName, PatternFillType.None, strokeWidth);
        drawdownChartPoints.value.push(pointOptionsObject);
    });

    return drawdownChartPoints;
};

function getAllocationInstruction(): FundAllocationInstruction {
    const fundAllocationCalcAtPresent = currentAllocations.value.getFundAllocationCalculation();
    const fundAllocationAtRetirement: FundAllocationCalculation = getAllocationInformationAtRetirement();

    const taxFreeLumpSumFundId: number|null = isTakingTaxFreeLumpSum.value ? fundInfo.moneyFund.id : null;

    let isValid = true;
    let invalidMessage = "";
    if (overallocatedAmount.value > 0) {
        isValid = false;
        invalidMessage = "Please check your fund allocations as they are currently over-allocated.";
    }

    return { 
        matchedYears: matchedPaymentsModel.value,
        matchedYearsInWindow: 0,
        taxFreeLumpSumFundId: taxFreeLumpSumFundId,
        fundAllocCalcAtPresent: fundAllocationCalcAtPresent,
        fundAllocCalcAtRetirement: fundAllocationAtRetirement,
        isValid,
        invalidMessage
    };
}

function getAllocationInformationAtRetirement(): FundAllocationCalculation {    
    const retirementAllocations = new CurrentAllocations();
    retirementAllocations.initialise(totalEstimatedValueAtRetirement.value, currentAllocations.value.taxFreeCashLumpSum);

    const moneyFund = totalEstimatedValueAtRetirement.value * (currentAllocations.value.moneyFundPercentage / 100);
    const incomeFund = totalEstimatedValueAtRetirement.value * (currentAllocations.value.incomeFundPercentage / 100);
    retirementAllocations.updateAllocations(moneyFund, incomeFund);

    return retirementAllocations.getFundAllocationCalculation();
}

function getDataPoint(y: number, colorName: string, patternFillType: PatternFillType, strokeWidth: number, name: string = ''): PointOptionsObject {
    let color: any = getPatternFillObject(patternFillType, colorName, strokeWidth);
    return { y, color, name };
}

function getPatternFillObject(patternFillType: PatternFillType, colorName: string, strokeWidth: number) {
    if (patternFillType == PatternFillType.None) {
        return colorName;
    }

    return {
        pattern: {
            path: {
                d: patternFills.getByType(patternFillType),
                strokeWidth: strokeWidth
            },
            width: 10,
            height: 10,
            color: colorName,
            opacity: 1
        }
    }; 
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(SubSection, {
    title: "What do the annual payments solely from my pension pots look like?",
    showHelp: false
  }, {
    default: _withCtx(() => [
      (isTakingTaxFreeLumpSum.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_1, " Based on the pots you wish to transfer, the very first payment from these will be the 25% tax free cash lump sum wanted at retirement. This is currently estimated as " + _toDisplayString(_unref(formatter).formatCurrency(_unref(currentAllocations).taxFreeCashLumpSum)) + ". ", 1))
        : _createCommentVNode("", true),
      _cache[7] || (_cache[7] = _createElementVNode("p", null, " All the annual pension pot payments currently wanted in your plan are shown in the chart below. ", -1)),
      _createElementVNode("p", null, [
        _createElementVNode("strong", null, "There are currently " + _toDisplayString(unmatchedPaymentYearsCount.value) + " years of payments shown in dark green.", 1),
        _createTextVNode(" These expected payments depend on selling assets from the " + _toDisplayString(_unref(fundInfo).responsibleGrowthFund.name) + " and investment returns from all funds. ", 1)
      ]),
      _createElementVNode("p", null, [
        _createElementVNode("strong", null, "There are currently " + _toDisplayString(matchedPaymentsModel.value) + " years of payments shown in a lighter green colour.", 1),
        _createTextVNode(" These expected payments can be met solely from the estimated funds in the " + _toDisplayString(_unref(fundInfo).moneyFund.name) + " and " + _toDisplayString(_unref(fundInfo).incomeFund.name) + " at retirement in the GuiidePath product.", 1),
        (isTakingTaxFreeLumpSum.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createTextVNode(" This includes any lump sum payment also.")
            ], 64))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(DrawdownChart, { chartData: drawdownChartPoints.value }, null, 8, ["chartData"])
        ])
      ]),
      _cache[8] || (_cache[8] = _createElementVNode("p", { style: {"font-size":"1.25rem !important"} }, [
        _createElementVNode("strong", null, "Understanding investment risk in drawdown")
      ], -1)),
      _createElementVNode("p", null, [
        _createTextVNode(" Most people need to rely on some investment return (and hence take risk) for all their expected payments to be met. You can choose any combination of the three types of Funds offered within " + _toDisplayString(_unref(Constants).Product.Name) + " with the rest, which you can model below. You can find more information on these type of funds and building a portfolio from ", 1),
        _cache[2] || (_cache[2] = _createElementVNode("a", {
          href: "https://www.moneyhelper.org.uk/en/pensions-and-retirement/building-your-retirement-pot/investing-in-retirement",
          target: "_blank"
        }, "MoneyHelper", -1)),
        _cache[3] || (_cache[3] = _createTextVNode(". "))
      ]),
      _createElementVNode("p", null, [
        _createElementVNode("strong", null, _toDisplayString(_unref(FundAllocationCalculator).TrusteeAccountPercentage) + "% of your total funds will always be held in the trustee bank account which is currently " + _toDisplayString(_unref(formatter).formatCurrency(_unref(currentAllocations).trusteeBankAccount)) + " based on the expected transfers.", 1)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("label", _hoisted_6, [
            _createElementVNode("strong", null, _toDisplayString(_unref(fundInfo).moneyFund.name), 1),
            _cache[4] || (_cache[4] = _createTextVNode(": "))
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(NumberInput, {
            ref: "currentAllocToMoneyFundInput",
            description: _unref(fundInfo).moneyFund.name,
            modelValue: currentAllocToMoneyFund.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentAllocToMoneyFund).value = $event)),
            max: 10000000,
            showLabel: false
          }, null, 8, ["description", "modelValue"])
        ])
      ]),
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("label", _hoisted_10, [
            _createElementVNode("strong", null, _toDisplayString(_unref(fundInfo).incomeFund.name), 1),
            _cache[5] || (_cache[5] = _createTextVNode(": "))
          ])
        ]),
        _createElementVNode("div", _hoisted_11, [
          _createVNode(NumberInput, {
            ref: "currentAllocToIncomeFundInput",
            description: _unref(fundInfo).incomeFund.name,
            modelValue: currentAllocToIncomeFund.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((currentAllocToIncomeFund).value = $event)),
            max: 10000000,
            showLabel: false
          }, null, 8, ["description", "modelValue"])
        ])
      ]),
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("label", _hoisted_14, [
            _createElementVNode("strong", null, _toDisplayString(_unref(fundInfo).responsibleGrowthFund.name), 1),
            _cache[6] || (_cache[6] = _createTextVNode(": "))
          ])
        ]),
        _createElementVNode("div", _hoisted_15, [
          _createTextVNode(_toDisplayString(_unref(formatter).formatCurrency(_unref(currentAllocations).growthFund < 0 ? 0 : _unref(currentAllocations).growthFund)) + " ", 1),
          (overallocatedAmount.value > 0)
            ? (_openBlock(), _createElementBlock("span", _hoisted_16, "(Over-allocated by " + _toDisplayString(_unref(formatter).formatCurrency(overallocatedAmount.value)) + ")", 1))
            : _createCommentVNode("", true)
        ])
      ]),
      _cache[9] || (_cache[9] = _createElementVNode("p", null, [
        _createElementVNode("strong", null, "It is key to understand your reliance on positive returns and more risky growth funds as downturns can occur at any time.")
      ], -1)),
      _cache[10] || (_cache[10] = _createElementVNode("p", null, " As you change the allocations above, the expected chart payments may change to a lighter colour. If they do, it shows these payments could be met using the expected value in the Money and Income funds at Retirement alone. The more in these funds the more payments change colour. ", -1)),
      _cache[11] || (_cache[11] = _createElementVNode("p", null, [
        _createElementVNode("strong", null, "The fewer that change colour the higher risk future payments may not be met."),
        _createTextVNode(" These rely on payments from growth funds and overall future investment returns. There is more risk if payments in the "),
        _createElementVNode("a", {
          href: "https://www.investopedia.com/terms/s/sequence-risk.asp",
          target: "_blank"
        }, "early years after retirement"),
        _createTextVNode(" do not change colour. Having to sell funds when they have fallen in value near the start of retirement can be very difficult to recover from. ")
      ], -1))
    ]),
    _: 1
  }))
}
}

})